import React, { useState, useRef } from 'react';
import './IngredientiOrdinabili.css';

interface Ingrediente {
  ingrediente_id: number;
  nome: string;
  quantita: number;
  unita: string;
  ordine?: number;
  is_variegatura?: boolean;
  is_aggiunta?: boolean;
}

interface IngredientiOrdinabiliProps {
  ingredienti: Ingrediente[];
  onReorder: (nuoviIngredienti: Ingrediente[]) => void;
  onUpdateIngrediente: (index: number, field: string, value: any) => void;
  onRemoveIngrediente: (index: number) => void;
  ingredientiDB: any[];
  className?: string;
}

const IngredientiOrdinabili: React.FC<IngredientiOrdinabiliProps> = ({
  ingredienti,
  onReorder,
  onUpdateIngrediente,
  onRemoveIngrediente,
  ingredientiDB,
  className = ''
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragCounter = useRef(0);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragCounter.current++;
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    dragCounter.current = 0;
    
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      const nuoviIngredienti = [...ingredienti];
      const ingredienteSpostato = nuoviIngredienti[draggedIndex];
      
      // Rimuovi l'elemento dalla posizione originale
      nuoviIngredienti.splice(draggedIndex, 1);
      
      // Inserisci l'elemento nella nuova posizione
      nuoviIngredienti.splice(dropIndex, 0, ingredienteSpostato);
      
      // Aggiorna gli indici di ordine
      const ingredientiConOrdine = nuoviIngredienti.map((ing, index) => ({
        ...ing,
        ordine: index
      }));
      
      onReorder(ingredientiConOrdine);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  };

  const moveUp = (index: number) => {
    if (index > 0) {
      const nuoviIngredienti = [...ingredienti];
      [nuoviIngredienti[index - 1], nuoviIngredienti[index]] = 
      [nuoviIngredienti[index], nuoviIngredienti[index - 1]];
      
      const ingredientiConOrdine = nuoviIngredienti.map((ing, idx) => ({
        ...ing,
        ordine: idx
      }));
      
      onReorder(ingredientiConOrdine);
    }
  };

  const moveDown = (index: number) => {
    if (index < ingredienti.length - 1) {
      const nuoviIngredienti = [...ingredienti];
      [nuoviIngredienti[index], nuoviIngredienti[index + 1]] = 
      [nuoviIngredienti[index + 1], nuoviIngredienti[index]];
      
      const ingredientiConOrdine = nuoviIngredienti.map((ing, idx) => ({
        ...ing,
        ordine: idx
      }));
      
      onReorder(ingredientiConOrdine);
    }
  };

  return (
    <div className={`ingredienti-ordinabili ${className}`}>
      {ingredienti.map((ing, index) => (
        <div
          key={`${ing.ingrediente_id}-${index}`}
          className={`ingrediente-row-ordinabile ${
            draggedIndex === index ? 'dragging' : ''
          } ${
            dragOverIndex === index ? 'drag-over' : ''
          }`}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnter={(e) => handleDragEnter(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
        >
          <div className="drag-handle" title="Trascina per riordinare">
            <span className="drag-icon">⋮⋮</span>
          </div>
          
          <div className="ordine-numero">
            {index + 1}
          </div>
          
          <select
            value={ing.ingrediente_id}
            onChange={(e) => onUpdateIngrediente(index, 'ingrediente_id', parseInt(e.target.value))}
            className="ingrediente-select"
          >
            {ingredientiDB.map(ingrediente => (
              <option key={ingrediente.id} value={ingrediente.id}>
                {ingrediente.nome}
              </option>
            ))}
          </select>
          
          <input
            type="number"
            placeholder="Quantità"
            value={ing.quantita}
            onChange={(e) => onUpdateIngrediente(index, 'quantita', parseFloat(e.target.value) || 0)}
            className="ingrediente-quantita-input"
            min="0"
            step="0.1"
          />
          
          <span className="ingrediente-unita">{ing.unita}</span>
          
          <select
            value={ing.is_variegatura ? 'variegatura' : (ing.is_aggiunta ? 'aggiunta' : 'miscela')}
            onChange={(e) => {
              const val = e.target.value;
              onUpdateIngrediente(index, 'is_variegatura', val === 'variegatura');
              onUpdateIngrediente(index, 'is_aggiunta', val === 'aggiunta');
            }}
            className={`tipo-select ${ing.is_variegatura ? 'variegatura' : (ing.is_aggiunta ? 'aggiunta' : 'miscela')}`}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="miscela">🍦 Miscela</option>
            <option value="variegatura">🌀 Variegatura</option>
            <option value="aggiunta">🍫 Aggiunta</option>
          </select>

          <div className="ordine-controls">
            <button
              type="button"
              onClick={() => moveUp(index)}
              disabled={index === 0}
              className="btn-ordine"
              title="Sposta su"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => moveDown(index)}
              disabled={index === ingredienti.length - 1}
              className="btn-ordine"
              title="Sposta giù"
            >
              ↓
            </button>
          </div>
          
          <button
            type="button"
            onClick={() => onRemoveIngrediente(index)}
            className="btn btn-danger btn-small"
            title="Rimuovi ingrediente"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default IngredientiOrdinabili;
